import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { orderingFetch } from '../lib/ordering-fetch'
import type { User } from '../types/ordering'

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  isAdmin: boolean
  isViewOnly: boolean
  isIntern: boolean
  isOfficeSupport: boolean
  isSalesRep: boolean
  canManageQuotes: boolean
  canManageUsers: boolean
  canAccessAdminData: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  refreshSession: () => Promise<void>
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  isAdmin: false,
  isViewOnly: false,
  isIntern: false,
  isOfficeSupport: false,
  isSalesRep: false,
  canManageQuotes: false,
  canManageUsers: false,
  canAccessAdminData: false,
  login: async () => {},
  logout: async () => {},
  refreshSession: async () => {},
})

export function useAuth() {
  return useContext(AuthContext)
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const isAuthenticated = user !== null
  const isAdmin = user?.role === 'admin'
  const isViewOnly = user?.role === 'view_only_admin'
  const isIntern = user?.role === 'intern'
  const isOfficeSupport = user?.role === 'office_support'
  const isSalesRep = user?.role === 'sales_rep'
  // Mirrors API can_manage_quotes (rbac.py): per-user override wins, else every
  // role except view_only_admin and intern — consultants/RMs included.
  const canManageQuotes = !!user && (user.perm_create_quotes ?? !['view_only_admin', 'intern'].includes(user.role))
  const canManageUsers = !!user && ['admin', 'office_support', 'regional_manager', 'consultant'].includes(user.role)
  const canAccessAdminData = isAdmin

  // Fetch session on mount
  useEffect(() => {
    let cancelled = false
    async function fetchSession() {
      try {
        const res = await orderingFetch('/api/auth/session')
        if (res.ok) {
          const data = await res.json()
          if (!cancelled) setUser(data.user)
        }
      } catch {
        // Session not available — user is not logged in
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    fetchSession()
    return () => { cancelled = true }
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const res = await orderingFetch('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Login failed' }))
      throw new Error(err.detail || 'Login failed')
    }
    const data = await res.json()
    setUser(data.user ?? data)
  }, [])

  const logout = useCallback(async () => {
    await orderingFetch('/api/auth/logout', { method: 'POST' })
    setUser(null)
  }, [])

  const refreshSession = useCallback(async () => {
    const res = await orderingFetch('/api/auth/session')
    if (res.ok) {
      const data = await res.json()
      setUser(data.user)
    }
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        isAdmin,
        isViewOnly,
        isIntern,
        isOfficeSupport,
        isSalesRep,
        canManageQuotes,
        canManageUsers,
        canAccessAdminData,
        login,
        logout,
        refreshSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
